import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import WeightInput, { fromCanonicalLb, toCanonicalLb } from "@/components/app/WeightInput";
import TimeInput from "@/components/app/TimeInput";
import UnitInput from "@/components/app/UnitInput";
import { fromCanonicalMeters, fromCanonicalInches, toCanonicalMeters, toCanonicalInches } from "@/lib/units";

/**
 * Edit-log dialog. Works on both per-set logs (`movement_name` set) and
 * block-completion logs (`block_id` set, no `movement_name`). Renders only
 * the fields relevant to the log's shape so the form stays tight.
 */
export default function EditLogDialog({ log, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isBlock = !log?.movement_name && !!log?.block_id;

  // Form state — initialised from the log doc. We split total_time into min+sec
  // for usability on block logs.
  const [form, setForm] = useState(() => initial(log));

  // Re-init when opening so toggling between rows always shows fresh values.
  useEffect(() => {
    if (open) setForm(initial(log));
  }, [open, log]);

  function initial(l) {
    if (!l) return {};
    const tt = l.total_time_seconds || 0;
    return {
      reps: l.reps ?? "",
      // Weight is stored canonical-lb; the WeightInput component handles unit conversion.
      weight_unit: l.weight_unit || "lb",
      weight: l.weight != null ? String(fromCanonicalLb(l.weight, l.weight_unit || "lb")) : "",
      time_seconds: l.time_seconds ?? "",
      distance: l.distance_meters != null
        ? String(fromCanonicalMeters(l.distance_meters, l.distance_unit || "m"))
        : "",
      distance_unit: l.distance_unit || "m",
      height: l.height_inches != null
        ? String(fromCanonicalInches(l.height_inches, l.height_unit || "in"))
        : "",
      height_unit: l.height_unit || "in",
      calories: l.calories ?? "",
      rpe: l.rpe ?? "",
      notes: l.notes ?? "",
      // Block-only:
      time_min: tt ? String(Math.floor(tt / 60)) : "",
      time_sec: tt ? String(tt % 60) : "",
      rounds_completed: l.rounds_completed ?? "",
      extra_reps: l.extra_reps ?? "",
    };
  }

  const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const patch = {};
      if (isBlock) {
        const min = numOrNull(form.time_min) || 0;
        const sec = numOrNull(form.time_sec) || 0;
        patch.total_time_seconds = (min || sec) ? (min * 60 + sec) : null;
        patch.rounds_completed = numOrNull(form.rounds_completed);
        patch.extra_reps = numOrNull(form.extra_reps);
        patch.distance_meters = toCanonicalMeters(form.distance, form.distance_unit);
        patch.distance_unit = form.distance_unit || "m";
        patch.height_inches = toCanonicalInches(form.height, form.height_unit);
        patch.height_unit = form.height_unit || "in";
        patch.calories = numOrNull(form.calories);
        patch.rpe = numOrNull(form.rpe);
        patch.notes = form.notes?.trim() || null;
      } else {
        patch.reps = numOrNull(form.reps);
        patch.weight = toCanonicalLb(form.weight, form.weight_unit);
        patch.weight_unit = form.weight_unit || "lb";
        patch.time_seconds = numOrNull(form.time_seconds);
        patch.distance_meters = toCanonicalMeters(form.distance, form.distance_unit);
        patch.distance_unit = form.distance_unit || "m";
        patch.height_inches = toCanonicalInches(form.height, form.height_unit);
        patch.height_unit = form.height_unit || "in";
        patch.calories = numOrNull(form.calories);
        patch.rpe = numOrNull(form.rpe);
        patch.notes = form.notes?.trim() || null;
      }
      const { data } = await api.patch(`/workout-logs/${log.id}`, patch);
      toast.success("Log updated");
      setOpen(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <span onClick={() => setOpen(true)} className="inline-flex" data-testid={`edit-log-trigger-${log.id}`}>
        {trigger || (
          <Button size="icon" variant="ghost"
                  className="rounded-lg h-7 w-7 text-ink-2 hover:bg-sage/40 shrink-0"
                  aria-label="Edit this entry">
            <PencilSimple size={12} />
          </Button>
        )}
      </span>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Edit {isBlock ? "section result" : "set"}
          </DialogTitle>
          <DialogDescription>
            {isBlock
              ? (log.block_title || log.block_type?.toUpperCase() || "Section")
              : log.movement_name}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="edit-log-form">
          {isBlock ? (
            <>
              <div>
                <Label className="hs-label">Total time</Label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <Input type="number" min="0" value={form.time_min}
                         onChange={(e) => setForm({ ...form, time_min: e.target.value })}
                         className="h-11 rounded-xl font-data" placeholder="min"
                         data-testid="edit-log-time-min" />
                  <Input type="number" min="0" max="59" value={form.time_sec}
                         onChange={(e) => setForm({ ...form, time_sec: e.target.value })}
                         className="h-11 rounded-xl font-data" placeholder="sec"
                         data-testid="edit-log-time-sec" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="hs-label">Rounds</Label>
                  <Input type="number" min="0" step="0.5" value={form.rounds_completed}
                         onChange={(e) => setForm({ ...form, rounds_completed: e.target.value })}
                         className="mt-2 h-11 rounded-xl font-data" data-testid="edit-log-rounds" />
                </div>
                <div>
                  <Label className="hs-label">+ Reps</Label>
                  <Input type="number" min="0" value={form.extra_reps}
                         onChange={(e) => setForm({ ...form, extra_reps: e.target.value })}
                         className="mt-2 h-11 rounded-xl font-data" data-testid="edit-log-extra-reps" />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="hs-label">Reps</Label>
                  <Input type="number" min="0" value={form.reps}
                         onChange={(e) => setForm({ ...form, reps: e.target.value })}
                         className="mt-2 h-11 rounded-xl font-data" data-testid="edit-log-reps" />
                </div>
                <div>
                  <WeightInput
                    value={form.weight}
                    onChange={(v, u) => setForm({ ...form, weight: v, weight_unit: u })}
                    unit={form.weight_unit}
                    testId="edit-log-weight"
                  />
                </div>
              </div>
              <TimeInput
                valueSeconds={form.time_seconds}
                onChangeSeconds={(v) => setForm({ ...form, time_seconds: v })}
                label="Time"
                testId="edit-log-time-seconds"
              />
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <UnitInput
                kind="distance"
                value={form.distance}
                onChange={(v, u) => setForm({ ...form, distance: v, distance_unit: u })}
                unit={form.distance_unit}
                testId="edit-log-distance"
              />
            </div>
            <div>
              <UnitInput
                kind="height"
                value={form.height}
                onChange={(v, u) => setForm({ ...form, height: v, height_unit: u })}
                unit={form.height_unit}
                testId="edit-log-height"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="hs-label">Calories</Label>
              <Input type="number" min="0" value={form.calories}
                     onChange={(e) => setForm({ ...form, calories: e.target.value })}
                     className="mt-2 h-11 rounded-xl font-data" data-testid="edit-log-calories" />
            </div>
            <div>
              <Label className="hs-label">RPE</Label>
              <Input type="number" step="0.5" min="0" max="10" value={form.rpe}
                     onChange={(e) => setForm({ ...form, rpe: e.target.value })}
                     className="mt-2 h-11 rounded-xl font-data" data-testid="edit-log-rpe" />
            </div>
          </div>

          <div>
            <Label className="hs-label">Notes</Label>
            <Textarea rows={2} value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="mt-2 rounded-xl" data-testid="edit-log-notes" />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}
                    className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={busy}
                    className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white"
                    data-testid="edit-log-save">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
